import React from 'react';
import { Layout } from './Home';
import PageHero from '../components/PageHero';
import RichContent from '../components/RichContent';

const CONTENT = `
## 1. Our Commitment to Your Privacy
**Emerald Corporates Limited** ("Emerald", "we", "us", "our") — a company incorporated in England & Wales (Company No. 16120279), with registered office at Office Gold, Building 7, Floor 5, 566 Chiswick High Road, Chiswick Business Park, London, United Kingdom, W4 5YG — is committed to protecting the privacy of individuals whose personal data we handle.

This Privacy Policy explains what personal data we collect, why we collect it, how we use it, how long we retain it, and the rights you have under the **UK General Data Protection Regulation (UK GDPR)**, the **Data Protection Act 2018**, and, where applicable, the **EU General Data Protection Regulation (EU GDPR)**.

## 2. Data Controller
For personal data processed through our website and corporate services, Emerald Corporates Limited is the **data controller** within the meaning of UK GDPR.

Data protection queries may be directed to: **info@emeraldcorporates.com**

## 3. Personal Data We Collect
Depending on how you interact with us, we may collect and process the following categories of personal data:

- **Contact data** — name, email address, phone number, postal address and job title, when you submit a contact form or subscribe to our updates
- **Correspondence** — messages, files and enquiry content you send to us
- **Authentication data** — email, username, and hashed password credentials for authenticated portal users; OAuth identifiers where you sign in via a third-party identity provider
- **Technical data** — IP address, browser type and version, device identifiers, operating system, referral source, pages visited and session duration
- **Cookies and analytics** — see Section 8 below

We do **not** knowingly collect personal data from children under the age of 16. If you believe a child has provided us with personal data, please contact us so we can delete it.

## 4. How We Use Your Data (Lawful Bases)
We process personal data on one or more of the following lawful bases under UK GDPR Article 6:

- **Consent** — where you have voluntarily submitted data through a contact form or subscription
- **Contract** — where processing is necessary to enter into or perform a contract with you or your employer
- **Legitimate interests** — for corporate communications, business development, service improvement, IT security, fraud prevention and reasonable direct-marketing to existing business contacts
- **Legal obligation** — where required by applicable law, regulation or a valid legal order

## 5. How We Protect Your Data — Data Security
We implement appropriate **technical and organisational measures** to protect personal data against unauthorised access, alteration, disclosure, loss or destruction, including:

- **Encryption in transit** using TLS 1.2 or higher for all web traffic
- **Encryption at rest** for sensitive fields and credentials, including hashed and salted password storage
- **Access controls** based on the principle of least privilege, with role-based permissions and multi-factor authentication for privileged accounts
- **Network segmentation** and firewalling of production infrastructure
- **Vulnerability management** — periodic patching, dependency scanning and independent VAPT
- **Security monitoring** — logging, alerting and 24×7 incident response for critical systems
- **Secure software development** practices, including code review, static analysis and secure-coding training
- **Data-loss prevention (DLP)** controls on sensitive corporate content
- **Backups and disaster-recovery** procedures aligned with our Business Continuity Plan
- **Vendor due diligence** on all data processors, with data-processing agreements in place

Despite these measures, no method of electronic transmission or storage is 100% secure; we cannot guarantee absolute security, but we take our responsibilities seriously and act promptly on any suspected breach.

## 6. Data Breach Notification
Where a personal data breach is likely to result in a risk to the rights and freedoms of individuals, we will notify the **UK Information Commissioner's Office (ICO)** without undue delay and, where feasible, within 72 hours of becoming aware. If the breach is likely to result in a **high** risk to affected individuals, we will also notify those individuals without undue delay.

## 7. Sharing Your Data
We share personal data only with:

- **Group companies and subsidiaries** of Emerald Corporates for coordinated service delivery
- **Trusted service providers (data processors)** — including hosting, email delivery, analytics and payment providers — under written data-processing agreements
- **Professional advisers** — auditors, lawyers, insurers — where necessary and confidential
- **Authorities** — where required to comply with law, court order or regulatory request
- **Business transfers** — in the event of a merger, acquisition or sale of assets, subject to appropriate confidentiality

We do **not** sell personal data to third parties.

## 8. Cookies and Similar Technologies
Our website uses a minimal set of cookies:

- **Strictly necessary cookies** — required for the site and authenticated sessions to function (e.g. session tokens)
- **Analytics cookies** — help us understand aggregate site usage in a privacy-respecting way
- **Preference cookies** — remember your consent choices

You may control cookies via your browser settings. Blocking strictly-necessary cookies may prevent parts of the site from functioning correctly.

## 9. International Data Transfers
Emerald operates across multiple jurisdictions. Where we transfer personal data outside the UK or European Economic Area, we ensure appropriate safeguards are in place, including:

- Adequacy decisions issued by the UK Government or European Commission
- Standard Contractual Clauses (SCCs) approved by the UK ICO or European Commission
- Binding corporate rules or supplementary technical measures where necessary

## 10. Data Retention
We retain personal data only as long as necessary for the purposes described in this Policy or as required by law. Typical retention periods:

- **Contact enquiries** — up to 24 months from last interaction
- **Newsletter subscriptions** — until you unsubscribe, plus 6 months in suppression lists
- **Authenticated user accounts** — for the duration of the account, plus 12 months after closure for audit purposes
- **Financial and regulatory records** — for the statutory retention period required by applicable law (typically 6–10 years)
- **Server access and security logs** — up to 12 months

## 11. Your Rights Under UK & EU GDPR
Subject to conditions and exemptions in law, you have the right to:

- **Access** — request a copy of the personal data we hold about you
- **Rectification** — ask us to correct inaccurate or incomplete data
- **Erasure ("right to be forgotten")** — ask us to delete your data where legally permissible
- **Restriction of processing** — ask us to limit how we use your data in certain circumstances
- **Portability** — receive your data in a structured, commonly-used, machine-readable format, and transmit it to another controller
- **Object** — object to processing based on legitimate interests, including direct marketing
- **Withdraw consent** — where processing is based on consent, at any time (without affecting prior lawful processing)
- **Complain** to a supervisory authority — the **UK Information Commissioner's Office (ICO)** at ico.org.uk, or the supervisory authority in your country of residence

To exercise any of these rights, please contact us at **info@emeraldcorporates.com**. We will respond within one month of receipt of a valid request (extendable by two further months for complex requests).

## 12. Automated Decision-Making
We do not use personal data for solely automated decisions producing legal or similarly significant effects on individuals.

## 13. Third-Party Websites
Our site may link to third-party websites. This Privacy Policy does not apply to those sites and we are not responsible for their privacy practices. We encourage you to read the privacy policies of any third-party sites you visit.

## 14. Changes to This Policy
We may update this Privacy Policy from time to time to reflect changes in law, technology or our services. Material changes will be highlighted on this page; the "Last updated" date reflects the current version.

## 15. Contact Us
Data protection queries, requests or complaints may be sent to:

**Emerald Corporates Limited**  
Office Gold, Building 7, Floor 5, 566 Chiswick High Road, Chiswick Business Park, London, United Kingdom, W4 5YG  
Email: **info@emeraldcorporates.com**

If you are not satisfied with our response, you have the right to lodge a complaint with the **UK Information Commissioner's Office** at [ico.org.uk](https://ico.org.uk).
`;

const PrivacyPage = () => {
  return (
    <Layout>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        description="How Emerald Corporates collects, uses, protects and shares your personal data — under UK & EU GDPR."
        image="https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=1600&q=80"
      />
      <section className="bg-[#e2ede6] py-16 lg:py-24" data-testid="privacy-page">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl border border-black/5 p-6 lg:p-10 shadow-sm">
            <p className="text-xs text-[#0b1a2e]/50 mb-6">
              Last updated: February 2026
            </p>
            <RichContent text={CONTENT} />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPage;
